/**
 * Contextual buyer checklist generator (Milestone 12).
 */
import { FAIR_PRICE_LABELS } from "../constants/automapFairPriceConstants";
import { RISK_FLAGS, RISK_LEVELS } from "../constants/automapVehicleRiskConstants";
import {
  BUYER_CHECKLIST_ITEMS,
  RISK_CONTEXTUAL_OVERRIDES,
  FAIR_PRICE_CONTEXTUAL_OVERRIDES,
  getChecklistItemById,
} from "../data/buyerChecklistContent";
import { deriveRiskFlagsFromListing } from "./vehicleRiskViewModel";
import { getBuyerVisibleIndicators, resolveRiskLevel } from "./vehicleRiskDisplay";
import { getContactChecklistReminderText } from "../intl/automotiveCopy";

const DEFAULT_CONTEXTUAL_IDS = [
  "confirm_availability",
  "confirm_price_valid",
  "compare_price",
  "property_card",
  "soat_valid",
  "mechanical_inspection",
  "avoid_advance_payment",
];

const RISK_FLAG_ALIASES = {
  pending_confirmation: RISK_FLAGS.AVAILABILITY_NOT_CONFIRMED,
  availability_unknown: RISK_FLAGS.AVAILABILITY_NOT_CONFIRMED,
  price_unusually_high: RISK_FLAGS.PRICE_OUTLIER_HIGH,
  price_unusually_low: RISK_FLAGS.PRICE_OUTLIER_LOW,
  fair_price_high: RISK_FLAGS.PRICE_OUTLIER_HIGH,
  below_market_caution: RISK_FLAGS.PRICE_OUTLIER_LOW,
  dealer_verification_pending: "dealer_unverified",
  fair_price_not_enough_data: "not_enough_data",
  low_quality_score: RISK_FLAGS.LISTING_QUALITY_LOW,
  missing_photos: RISK_FLAGS.MISSING_PHOTOS,
  few_photos: RISK_FLAGS.FEW_PHOTOS,
};

function normalizeRiskFlag(flag) {
  if (!flag) return null;
  const key = String(flag);
  return RISK_FLAG_ALIASES[key] || key;
}

function collectRiskTypes(listing, riskVm) {
  const types = new Set();
  deriveRiskFlagsFromListing(listing).forEach((flag) => types.add(normalizeRiskFlag(flag)));
  (riskVm?.flags || []).forEach((flag) => types.add(normalizeRiskFlag(flag)));
  getBuyerVisibleIndicators(listing).forEach((indicator) => {
    if (indicator.type) types.add(normalizeRiskFlag(indicator.type));
  });
  return types;
}

function collectListingSignals(listing, fairPriceVm, dealerVm) {
  const signals = new Set();
  if (!listing) return signals;

  const imageCount = listing.imageCount ?? listing.images?.length ?? listing.photos?.length ?? 0;
  if (!imageCount) signals.add("missingPhotos");
  if (imageCount > 0 && imageCount < 3) signals.add("fewPhotos");
  if (listing.mileageKm == null) signals.add("missingMileage");

  const label = listing.fairPriceLabel || fairPriceVm?.label;
  if (label === FAIR_PRICE_LABELS.high_price || label === FAIR_PRICE_LABELS.slightly_high) {
    signals.add("fairPriceHigh");
  }
  if (label === FAIR_PRICE_LABELS.below_market) signals.add("belowMarket");
  if (label === FAIR_PRICE_LABELS.not_enough_data || label === "unknown") signals.add("notEnoughData");
  if (label === FAIR_PRICE_LABELS.fair_price) signals.add("fairPrice");

  if (dealerVm) {
    if (!dealerVm.isVerified) signals.add("dealerUnverified");
    if (!dealerVm.canContact) signals.add("dealerContactMissing");
  } else if (!listing.dealerVerified) {
    signals.add("dealerUnverified");
  }

  if (listing.hasIncompleteInfo) signals.add("lowQuality");
  return signals;
}

function isStaleListing(listing) {
  if (!listing?.lastConfirmedAt) return true;
  const days = (Date.now() - Date.parse(listing.lastConfirmedAt)) / (1000 * 60 * 60 * 24);
  return Number.isNaN(days) || days > 14;
}

function scoreItem(item, riskTypes, listingSignals, riskLevel, listing, fairPriceVm, dealerVm) {
  let score = 0;
  if (item.defaultVisible) score += 2;

  for (const indicator of item.relatedRiskIndicators || item.relatedRiskFlags || []) {
    const normalized = normalizeRiskFlag(indicator);
    if (riskTypes.has(normalized) || riskTypes.has(indicator)) score += 5;
  }

  for (const label of item.relatedFairPriceLabels || []) {
    const listingLabel = listing?.fairPriceLabel || fairPriceVm?.label;
    if (listingLabel === label) score += 4;
  }

  for (const field of item.relatedListingFields || []) {
    if (listingSignals.has(field)) score += 4;
  }

  if (item.importance === "essential") score += 1;
  if (riskLevel === RISK_LEVELS.HIGH && item.buyerAction === "avoid_payment") score += 2;
  if (riskLevel === RISK_LEVELS.HIGH && item.phase === "inspection") {
    score += 5;
  }
  if ([RISK_LEVELS.MEDIUM, RISK_LEVELS.HIGH].includes(riskLevel) && item.id === "mechanical_inspection") {
    score += 6;
  }
  if (isStaleListing(listing) && item.id === "confirm_availability") score += 4;
  if (dealerVm && !dealerVm.canContact && item.id === "verify_dealer_identity") score += 3;
  return score;
}

function applyContextOverrides(item, riskTypes, fairPriceVm, listing) {
  for (const indicator of item.relatedRiskIndicators || item.relatedRiskFlags || []) {
    const normalized = normalizeRiskFlag(indicator);
    if (riskTypes.has(normalized) && RISK_CONTEXTUAL_OVERRIDES[normalized]) {
      return { ...item, ...RISK_CONTEXTUAL_OVERRIDES[normalized], sourceIndicator: normalized };
    }
    if (riskTypes.has(indicator) && RISK_CONTEXTUAL_OVERRIDES[indicator]) {
      return { ...item, ...RISK_CONTEXTUAL_OVERRIDES[indicator], sourceIndicator: indicator };
    }
  }

  const label = listing?.fairPriceLabel || fairPriceVm?.label;
  if (label && FAIR_PRICE_CONTEXTUAL_OVERRIDES[label]) {
    return { ...item, ...FAIR_PRICE_CONTEXTUAL_OVERRIDES[label], sourceFairPrice: label };
  }

  for (const type of riskTypes) {
    if (RISK_CONTEXTUAL_OVERRIDES[type]) {
      const base = item.relatedRiskIndicators?.includes(type) ? item : getMappedItemForRisk(type);
      if (base) return { ...base, ...RISK_CONTEXTUAL_OVERRIDES[type], sourceIndicator: type };
    }
  }
  return item;
}

function getMappedItemForRisk(riskType) {
  return (
    BUYER_CHECKLIST_ITEMS.find(
      (item) =>
        item.relatedRiskIndicators?.includes(riskType) || item.relatedRiskFlags?.includes(riskType)
    ) || null
  );
}

export function buildContextualChecklistItems(
  listing,
  { limit = 7, riskVm = null, fairPriceVm = null, dealerVm = null } = {}
) {
  if (!listing) {
    return DEFAULT_CONTEXTUAL_IDS.map((id) => getChecklistItemById(id)).filter(Boolean);
  }

  const riskTypes = collectRiskTypes(listing, riskVm);
  const listingSignals = collectListingSignals(listing, fairPriceVm, dealerVm);
  const riskLevel = riskVm?.level || resolveRiskLevel(listing);

  const ranked = BUYER_CHECKLIST_ITEMS.filter(
    (item) => !item.contextualOnly || scoreItem(item, riskTypes, listingSignals, riskLevel, listing, fairPriceVm, dealerVm) >= 4
  )
    .map((item) => ({
      item: applyContextOverrides(item, riskTypes, fairPriceVm, listing),
      score: scoreItem(item, riskTypes, listingSignals, riskLevel, listing, fairPriceVm, dealerVm),
    }))
    .sort((a, b) => b.score - a.score);

  const seen = new Set();
  const selected = [];
  for (const entry of ranked) {
    if (seen.has(entry.item.id)) continue;
    if (entry.score <= 0 && !entry.item.defaultVisible) continue;
    seen.add(entry.item.id);
    selected.push(entry.item);
    if (selected.length >= limit) break;
  }

  if (selected.length < 4) {
    for (const id of DEFAULT_CONTEXTUAL_IDS) {
      if (selected.length >= limit) break;
      const item = getChecklistItemById(id);
      if (item && !seen.has(item.id)) {
        seen.add(item.id);
        selected.push(applyContextOverrides(item, riskTypes, fairPriceVm, listing));
      }
    }
  }

  if ([RISK_LEVELS.MEDIUM, RISK_LEVELS.HIGH].includes(riskLevel)) {
    const peritaje = getChecklistItemById("mechanical_inspection");
    if (peritaje && !seen.has(peritaje.id)) {
      const enriched = applyContextOverrides(peritaje, riskTypes, fairPriceVm, listing);
      if (selected.length >= limit) selected.pop();
      selected.unshift(enriched);
      seen.add(peritaje.id);
    }
  }

  return selected.slice(0, limit);
}

export function hasContextualRiskReminders(listing) {
  const level = resolveRiskLevel(listing);
  return (
    getBuyerVisibleIndicators(listing).length > 0 ||
    [RISK_LEVELS.MEDIUM, RISK_LEVELS.HIGH, RISK_LEVELS.UNKNOWN].includes(level)
  );
}

export function shouldShowContactChecklistReminder(listing) {
  const level = resolveRiskLevel(listing);
  return [RISK_LEVELS.MEDIUM, RISK_LEVELS.HIGH].includes(level);
}

export function getContactChecklistReminderCopy(listing, dealerVm = null, t = null) {
  const level = resolveRiskLevel(listing);
  if (t) {
    return getContactChecklistReminderText(level, dealerVm, t);
  }
  if ([RISK_LEVELS.MEDIUM, RISK_LEVELS.HIGH].includes(level)) {
    return "Antes de avanzar, revisa las señales y considera un peritaje.";
  }
  if (dealerVm && !dealerVm.isVerified) {
    return "Antes de visitar, confirma dirección, asesor, documentos y condiciones.";
  }
  return "Antes de visitar, confirma disponibilidad, kilometraje, documentos y condiciones.";
}
